<template>
  <div class="ncrm-privacy-record-tab">
    <NcrmPrivacyInvitationAction v-if="!forbidden" :subject-type="subjectType" :subject-id="subjectId"
      :disabled="loading || Boolean(errorMessage)" @sent="fetchData" />
    <div v-if="loading" class="py-4">
      <v-skeleton-loader type="heading, paragraph, table-row-divider@3, list-item-two-line@3" />
    </div>
    <v-alert v-else-if="errorMessage" :type="forbidden ? 'warning' : 'error'">
      {{ errorMessage }}
      <template v-if="!forbidden" #append>
        <v-btn variant="text" @click="fetchData">{{ $t("ncrm.contacts.dataProtection.record.retry") }}</v-btn>
      </template>
    </v-alert>
    <template v-else>
      <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-5">
        <div>
          <div class="text-caption text-medium-emphasis mb-1">
            {{ $t("ncrm.contacts.dataProtection.record.summary") }}
          </div>
          <NcrmPrivacyStatusChip
            :state="summary?.summary_state"
            :breakdown="summary?.breakdown"
            :total="summary?.contact_points_total"
            :unclassified="summary?.contact_points_unclassified"
          />
        </div>
        <div class="d-flex flex-wrap align-center justify-end ga-3">
          <div class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.dataProtection.record.lastChange") }}:
            <strong>{{ formatDateTime(summary?.last_event_at) }}</strong>
          </div>
          <v-btn color="primary" prepend-icon="mdi-file-sign" @click="openEvidence($event)">
            {{ $t("ncrm.contacts.dataProtection.record.evidence.open") }}
          </v-btn>
        </div>
      </div>

      <template v-if="isCompany">
        <NcrmPrivacyCompanyContactPoints
          ref="contactPoints"
          :company-id="subjectId"
          :can-manage="canManagePrivacy"
          @record-objection="openObjection"
          @changed="handleContactPointsChanged"
        />
        <v-divider class="my-5" />
      </template>

      <section>
        <h3 class="text-subtitle-1 mb-1">{{ $t("ncrm.contacts.dataProtection.record.matrix.title") }}</h3>
        <p class="text-body-2 text-medium-emphasis mb-3">
          {{ $t("ncrm.contacts.dataProtection.record.matrix.hint") }}
        </p>
        <div v-if="states.length" class="ncrm-privacy-matrix">
          <div class="ncrm-privacy-matrix__header" aria-hidden="true">
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.purpose") }}</span>
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.contactPoint") }}</span>
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.channel") }}</span>
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.basis") }}</span>
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.state") }}</span>
            <span>{{ $t("ncrm.contacts.dataProtection.record.matrix.updated") }}</span>
          </div>
          <button
            v-for="item in states"
            :key="item.id"
            type="button"
            class="ncrm-privacy-matrix__row"
            @click="openDetails(item, $event)"
          >
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.purpose')">
              {{ item.purpose_name || emptyLabel }}
            </span>
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.contactPoint')">
              {{ item.contact_point_value || emptyLabel }}
            </span>
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.channel')">
              {{ labelFor("channels", item.channel_code) }}
            </span>
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.basis')">
              {{ labelFor("bases", item.lawful_basis_code) }}
            </span>
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.state')">
              <v-chip :color="stateStatus(item.state).color" size="x-small">
                <v-icon start size="12">{{ stateStatus(item.state).icon }}</v-icon>
                {{ labelFor("states", item.state) }}
              </v-chip>
            </span>
            <span :data-label="$t('ncrm.contacts.dataProtection.record.matrix.updated')">
              {{ formatDateTime(item.updated_at) }}
              <v-icon size="16" class="ml-1 text-medium-emphasis">mdi-chevron-right</v-icon>
            </span>
          </button>
        </div>
        <v-alert v-else type="info">
          {{ $t("ncrm.contacts.dataProtection.record.matrix.empty") }}
        </v-alert>
      </section>

      <v-divider class="my-5" />
      <NcrmPrivacyMailjetSyncStatus :subject-type="subjectType" :subject-id="subjectId" @record-objection="recordObjection" />
      <template v-if="canManagePrivacy">
        <v-divider class="my-5" />
        <NcrmPrivacyPreferenceLinks :subject-type="subjectType" :subject-id="subjectId" />
        <NcrmPrivacyDsarExportAction v-if="subjectType === 'person'" :person-id="subjectId" class="mt-5" />
      </template>
      <v-divider class="my-5" />

      <section>
        <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-3">
          <div>
            <h3 class="text-subtitle-1">{{ $t("ncrm.contacts.dataProtection.record.timeline.title") }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-0">
              {{ $t("ncrm.contacts.dataProtection.record.timeline.hint") }}
            </p>
          </div>
          <v-select
            v-if="events.length"
            v-model="eventFilter"
            :items="eventFilterOptions"
            item-title="title"
            item-value="value"
            :label="$t('ncrm.contacts.dataProtection.record.timeline.filter')"
            density="compact"
            hide-details
            class="ncrm-privacy-record-tab__filter"
          />
        </div>
        <v-list v-if="filteredEvents.length" lines="three" class="pa-0">
          <v-list-item v-for="event in filteredEvents" :key="event.id" class="px-0">
            <template #prepend>
              <v-avatar :color="stateStatus(eventState(event.event_type)).color" size="30" variant="tonal">
                <v-icon size="16">{{ stateStatus(eventState(event.event_type)).icon }}</v-icon>
              </v-avatar>
            </template>
            <v-list-item-title>{{ labelFor("events", event.event_type) }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ event.purpose_name || labelFor("categories", event.purpose_category) }} ·
              {{ event.contact_point_value || $t("ncrm.contacts.dataProtection.record.recordScope") }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              {{ formatDateTime(event.occurred_at) }} · {{ labelFor("sources", event.source_type) }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
        <v-alert v-else type="info">
          {{ $t("ncrm.contacts.dataProtection.record.timeline.empty") }}
        </v-alert>
        <div v-if="hasMore" class="text-caption text-medium-emphasis mt-2">
          {{ $t("ncrm.contacts.dataProtection.record.latestOnly") }}
        </div>
      </section>
    </template>

    <NcrmPrivacyEvidenceDrawer v-model="evidenceOpen" :subject-type="subjectType" :subject-id="subjectId" :default-event-type="evidenceEventType" @saved="handleEvidenceSaved" />

    <NcrmPrivacyEvidenceDrawer v-if="isCompany" v-model="objectionOpen"
      scope="company_objection" :subject-type="subjectType" :subject-id="subjectId"
      @saved="handleObjectionSaved" />

    <v-snackbar v-model="evidenceSaved" color="success" :timeout="3500">
      {{ $t("ncrm.contacts.dataProtection.record.evidence.saved") }}
    </v-snackbar>

    <v-snackbar v-model="doiSent" :color="doiSentOutcome === 'accepted' ? 'success' : 'warning'" :timeout="5000">
      {{ $t(`ncrm.contacts.dataProtection.record.doiResend.sent.${doiSentOutcome}`) }}
    </v-snackbar>

    <v-snackbar v-model="contactPointsSaved" color="success" :timeout="3500">
      {{ $t(`ncrm.contacts.dataProtection.record.contactPoints.${contactPointsSavedKey}`) }}
    </v-snackbar>

    <teleport to="body">
      <v-navigation-drawer v-model="detailOpen" location="right" temporary width="520">
      <div class="pa-5">
        <div class="d-flex align-center justify-space-between ga-3 mb-4">
          <div>
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.contacts.dataProtection.record.details.eyebrow") }}
            </div>
            <h3 class="text-h6">{{ selectedState?.purpose_name || emptyLabel }}</h3>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="detailOpen = false" />
        </div>
        <v-list v-if="selectedState" lines="two" class="pa-0">
          <v-list-item v-for="field in selectedStateFields" :key="field.label" class="px-0">
            <v-list-item-subtitle>{{ field.label }}</v-list-item-subtitle>
            <v-list-item-title>{{ field.value }}</v-list-item-title>
          </v-list-item>
        </v-list>
        <v-divider class="my-4" />
        <h4 class="text-subtitle-1 mb-2">{{ $t("ncrm.contacts.dataProtection.record.details.relatedEvents") }}</h4>
        <v-list v-if="selectedStateEvents.length" lines="two" class="pa-0">
          <v-list-item v-for="event in selectedStateEvents" :key="event.id" class="px-0">
            <v-list-item-title>{{ labelFor("events", event.event_type) }}</v-list-item-title>
            <v-list-item-subtitle>{{ formatDateTime(event.occurred_at) }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
        <div v-else class="text-body-2 text-medium-emphasis">
          {{ $t("ncrm.contacts.dataProtection.record.details.noRelatedEvents") }}
        </div>
        <!-- Montat numai cât drawer-ul e deschis: fiecare deschidere recitește starea de retrimitere. -->
        <template v-if="detailOpen && canResendDoi">
          <v-divider class="my-4" />
          <NcrmPrivacyDoiResendPanel :subject-type="subjectType" :subject-id="subjectId" :pair="selectedState" @sent="handleDoiSent" />
        </template>
      </div>
      </v-navigation-drawer>
    </teleport>
  </div>
</template>

<script>
import axios from "axios";
import { notifyPrivacyContactabilityChanged } from "@/Utile/ncrmPrivacyContactability.mjs"; // GDPR-122: chipurile de pe card
import NcrmPrivacyInvitationAction from "./NcrmPrivacyInvitationAction.vue";
import NcrmPrivacyCompanyContactPoints from "@/components/ncrm/shared/NcrmPrivacyCompanyContactPoints.vue";
import NcrmPrivacyDoiResendPanel from "@/components/ncrm/shared/NcrmPrivacyDoiResendPanel.vue";
import NcrmPrivacyEvidenceDrawer from "@/components/ncrm/shared/NcrmPrivacyEvidenceDrawer.vue";
import NcrmPrivacyStatusChip from "@/components/ncrm/shared/NcrmPrivacyStatusChip.vue";
import NcrmPrivacyPreferenceLinks from "@/components/ncrm/shared/NcrmPrivacyPreferenceLinks.vue";
import NcrmPrivacyDsarExportAction from "@/components/ncrm/shared/NcrmPrivacyDsarExportAction.vue";
import NcrmPrivacyMailjetSyncStatus from "@/components/ncrm/shared/NcrmPrivacyMailjetSyncStatus.vue";
import { hasPrivacyManagerRole, hasPrivacyRegistryRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import { resolvePrivacyStatus } from "@/Utile/ncrmPrivacyStatus.mjs";

const STATE_SUMMARY = Object.freeze({
  granted: "active", confirmed: "active", pending_confirmation: "pending", other_basis: "other_basis",
  refused: "withdrawn_or_refused", withdrawn: "withdrawn_or_refused", objected: "withdrawn_or_refused",
  restricted: "withdrawn_or_refused", expired: "undocumented",
});

export default {
  name: "NcrmPrivacyRecordTab",
  components: { NcrmPrivacyInvitationAction, NcrmPrivacyCompanyContactPoints, NcrmPrivacyDoiResendPanel, NcrmPrivacyEvidenceDrawer, NcrmPrivacyStatusChip,
    NcrmPrivacyPreferenceLinks, NcrmPrivacyMailjetSyncStatus, NcrmPrivacyDsarExportAction },
  props: {
    subjectType: { type: String, required: true, validator: (value) => ["person", "company"].includes(value) },
    subjectId: { type: [String, Number], required: true },
  },
  data: () => ({
    loading: false, errorMessage: "", forbidden: false, summary: null, states: [], events: [],
    eventFilter: "all", detailOpen: false, evidenceOpen: false, evidenceSaved: false, evidenceEventType: null,
    canResendDoiRole: false, doiSent: false, doiSentOutcome: "accepted",
    objectionOpen: false, contactPointsSaved: false, contactPointsSavedKey: "classify.saved",
    canManagePrivacy: false,
    selectedState: null, lastTrigger: null, requestSequence: 0,
    hasMore: false,
  }),
  computed: {
    subjectKey() { return `${this.subjectType}:${this.subjectId}`; },
    isCompany() { return this.subjectType === "company"; },
    emptyLabel() { return this.$t("ncrm.contacts.details.emptyValue"); },
    filteredEvents() {
      return this.eventFilter === "all" ? this.events : this.events.filter((item) => item.event_type === this.eventFilter);
    },
    eventFilterOptions() {
      const types = [...new Set(this.events.map((item) => item.event_type).filter(Boolean))];
      return [{ title: this.$t("ncrm.contacts.dataProtection.record.timeline.all"), value: "all" },
        ...types.map((value) => ({ title: this.labelFor("events", value), value }))];
    },
    selectedStateEvents() {
      if (!this.selectedState) return [];
      const subjectIdKey = this.subjectType === "person" ? "ncrm_person_id" : "ncrm_company_id";
      return this.events.filter((event) => {
        const targetApplies = Number(event.contact_point_id) === Number(this.selectedState.contact_point_id)
          || (event.scope_type === "subject" && event.subject_type === this.subjectType
            && Number(event[subjectIdKey]) === Number(this.subjectId));
        const channelApplies = event.channel_code === null || event.channel_code === this.selectedState.channel_code;
        const purposeApplies = Number(event.purpose_id) === Number(this.selectedState.purpose_id)
          || (event.purpose_id === null && (event.purpose_category === null
            || event.purpose_category === this.selectedState.purpose_category));
        return targetApplies && channelApplies && purposeApplies;
      });
    },
    // GDPR S6.5: retrimiterea DOI apare numai pe o pereche email „în așteptare”, rolurilor din registru.
    canResendDoi() {
      return Boolean(this.canResendDoiRole && this.selectedState?.state === "pending_confirmation"
        && this.selectedState?.channel_code === "email");
    },
    selectedStateFields() {
      if (!this.selectedState) return [];
      const item = this.selectedState;
      return [
        ["contactPoint", item.contact_point_value], ["channel", this.labelFor("channels", item.channel_code)],
        ["state", this.labelFor("states", item.state)], ["basis", this.labelFor("bases", item.lawful_basis_code)],
        ["assurance", this.labelFor("assurance", item.assurance_level)], ["effectiveFrom", this.formatDateTime(item.effective_from)],
        ["expiresAt", this.formatDateTime(item.expires_at)], ["updated", this.formatDateTime(item.updated_at)],
      ].map(([key, value]) => ({ label: this.$t(`ncrm.contacts.dataProtection.record.details.${key}`), value: value || this.emptyLabel }));
    },
  },
  watch: {
    subjectKey: { immediate: true, handler() { this.handleSubjectChange(); } },
    detailOpen(value) { if (!value) this.$nextTick(this.restoreFocus); },
    evidenceOpen(value) { if (!value) this.$nextTick(this.restoreFocus); },
    objectionOpen(value) { if (!value) this.$nextTick(this.restoreFocus); },
  },
  created() {
    this.canManagePrivacy = hasPrivacyManagerRole();
    this.canResendDoiRole = hasPrivacyRegistryRole();
  },
  methods: {
    handleSubjectChange() {
      this.lastTrigger = null; this.evidenceSaved = false; this.contactPointsSaved = false;
      this.detailOpen = false;
      this.evidenceOpen = false;
      this.objectionOpen = false;
      this.selectedState = null;
      this.eventFilter = "all";
      this.fetchData();
    },
    async fetchData() {
      const sequence = ++this.requestSequence;
      this.loading = true; this.errorMessage = ""; this.forbidden = false;
      const params = { subject_type: this.subjectType, subject_id: this.subjectId, limit: 100 };
      try {
        const [summary, states, events] = await Promise.all([
          axios.get("/ncrm/privacy/summary", { params: { subject_type: this.subjectType, subject_id: this.subjectId } }),
          axios.get("/ncrm/privacy/states", { params }), axios.get("/ncrm/privacy/events", { params }),
        ]);
        if (sequence !== this.requestSequence) return;
        this.summary = summary.data || null;
        this.states = Array.isArray(states.data?.items) ? states.data.items : [];
        this.events = Array.isArray(events.data?.items) ? events.data.items : [];
        this.hasMore = Boolean(states.data?.pagination?.has_more || events.data?.pagination?.has_more);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.forbidden = Number(error?.response?.status) === 403;
        this.errorMessage = this.$t(`ncrm.contacts.dataProtection.record.${this.forbidden ? "noPermission" : "loadError"}`);
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    labelFor(group, value) {
      if (!value) return this.emptyLabel;
      const key = `ncrm.contacts.dataProtection.record.${group}.${String(value).toLowerCase()}`;
      if (typeof this.$te === "function" && !this.$te(key)) return String(value).replaceAll("_", " ");
      const translated = this.$t(key);
      return translated === key ? String(value).replaceAll("_", " ") : translated;
    },
    stateStatus(value) { return resolvePrivacyStatus(STATE_SUMMARY[value] || "undocumented"); },
    eventState(value) { return value; },
    // Succes: drawer-ul se închide, datele se reîncarcă, feedback non-intruziv (`docs/design.md`).
    handleDoiSent(outcome) {
      this.doiSentOutcome = outcome === "uncertain" ? "uncertain" : "accepted";
      this.detailOpen = false;
      this.doiSent = true;
      this.fetchData();
    },
    formatDateTime(value) {
      if (!value) return this.emptyLabel;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return this.emptyLabel;
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    openDetails(item, event) { this.selectedState = item; this.lastTrigger = event?.currentTarget || null; this.detailOpen = true; },
    openEvidence(event, type = null) { this.lastTrigger = event?.currentTarget || null; this.evidenceEventType = type; this.evidenceOpen = true; },
    openObjection(event) { this.lastTrigger = event?.currentTarget || null; this.objectionOpen = true; },
    recordObjection(event) { return this.isCompany ? this.openObjection(event) : this.openEvidence(event, "objected"); }, // S8.5c
    notifyContactPointsSaved(key) { this.contactPointsSavedKey = key; this.contactPointsSaved = true; },
    handleContactPointsChanged() {
      this.notifyContactPointsSaved("classify.saved");
      this.fetchData(); notifyPrivacyContactabilityChanged();
    },
    handleObjectionSaved({ subjectKey } = {}) {
      if (subjectKey !== this.subjectKey) return;
      this.objectionOpen = false;
      this.notifyContactPointsSaved("objection.saved");
      // Secțiunea se reîncarcă înaintea recordului: `fetchData` ridică starea de
      // loading, iar ref-ul nu are voie să depindă de momentul re-randării.
      this.$refs.contactPoints?.fetchItems?.();
      this.fetchData(); notifyPrivacyContactabilityChanged();
    },
    handleEvidenceSaved({ subjectKey } = {}) {
      if (subjectKey !== this.subjectKey) return;
      this.evidenceOpen = false;
      this.evidenceSaved = true; this.fetchData(); notifyPrivacyContactabilityChanged();
    },
    restoreFocus() { this.lastTrigger?.focus?.(); this.lastTrigger = null; },
  },
};
</script>

<style scoped>
.ncrm-privacy-matrix { border: 1px solid rgba(var(--v-theme-on-surface), 0.08); border-radius: var(--app-radius-base); overflow: hidden; }
.ncrm-privacy-matrix__header, .ncrm-privacy-matrix__row { display: grid; grid-template-columns: 1.1fr 1.3fr .7fr .9fr .9fr 1fr; gap: 12px; align-items: center; padding: 10px 12px; }
.ncrm-privacy-matrix__header { background: rgba(var(--v-theme-on-surface), 0.04); font-size: .75rem; font-weight: 600; text-transform: uppercase; }
.ncrm-privacy-matrix__row { width: 100%; border: 0; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); color: inherit; background: transparent; font: inherit; text-align: left; cursor: pointer; }
.ncrm-privacy-matrix__row:hover { background: rgba(var(--v-theme-primary), 0.04); }
.ncrm-privacy-matrix__row:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: -2px; }
.ncrm-privacy-record-tab__filter { max-width: 260px; }
@media (max-width: 700px) {
  .ncrm-privacy-matrix__header { display: none; }
  .ncrm-privacy-matrix__row { grid-template-columns: 1fr; gap: 6px; padding: 14px; }
  .ncrm-privacy-matrix__row > span::before { content: attr(data-label) ": "; font-size: .75rem; opacity: .68; }
  .ncrm-privacy-record-tab__filter { max-width: none; width: 100%; }
}
</style>
